/**
 * Content API endpoints.
 *
 * All content is site-scoped. Each API call requires a siteSlug.
 */

import { api } from './client';
import type {
  Post,
  Page,
  PageEditorMode,
  Media,
  MediaFolder,
  Tag,
  ApiResponse,
  ListResponse,
  ItemResponse,
  ListParams,
  ContentData,
} from '../types';
import type { StorageType } from './storageConnections';

/** Broad file-type buckets the media list can be filtered by */
export type MediaKind = 'image' | 'document' | 'font' | 'video' | 'audio';

/** Parameters for listing media items */
export interface MediaListParams extends ListParams {
  folder?: MediaFolder;
  origin?: 'site' | 'baxter';
  /** Filter by storage path (for looking up media by URL) */
  storage_path?: string;
  /** Filter by broad file type (image/document/font/video/audio) */
  kind?: MediaKind;
}

/** Build query string from params */
function buildQuery(params: ListParams): string {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.per_page) query.set('per_page', String(params.per_page));
  if (params.status) query.set('status', params.status);
  if (params.sort) query.set('sort', params.sort);
  const str = query.toString();
  return str ? `?${str}` : '';
}

/** Build query string for media list params */
function buildMediaQuery(params: MediaListParams): string {
  const query = new URLSearchParams();
  if (params.page) query.set('page', String(params.page));
  if (params.per_page) query.set('per_page', String(params.per_page));
  if (params.folder) query.set('folder', params.folder);
  if (params.origin) query.set('origin', params.origin);
  if (params.storage_path) query.set('storage_path', params.storage_path);
  if (params.kind) query.set('kind', params.kind);
  const str = query.toString();
  return str ? `?${str}` : '';
}

/** Posts API */
export const postsApi = {
  /** List posts with pagination */
  list: (siteSlug: string, params: ListParams = {}) =>
    api.get<ListResponse<Post>>(`/v1/sites/${siteSlug}/content/posts${buildQuery(params)}`),

  /** Get a single post by ID */
  get: (siteSlug: string, id: string) =>
    api.get<ItemResponse<Post>>(`/v1/sites/${siteSlug}/content/posts/${id}`),

  /** Get a post by slug */
  getBySlug: (siteSlug: string, slug: string) =>
    api.get<ItemResponse<Post>>(`/v1/sites/${siteSlug}/content/posts/slug/${slug}`),

  /** Create a new post */
  create: (siteSlug: string, data: ContentData<Post>) =>
    api.post<ItemResponse<Post>>(`/v1/sites/${siteSlug}/content/posts`, data),

  /** Update an existing post */
  update: (siteSlug: string, id: string, data: ContentData<Post>) =>
    api.put<ItemResponse<Post>>(`/v1/sites/${siteSlug}/content/posts/${id}`, data),

  /** Delete a post */
  delete: (siteSlug: string, id: string) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/content/posts/${id}`),

  /** Preview a full post with base.html wrapper */
  previewFull: (
    siteSlug: string,
    data: {
      title: string;
      content: string;
      slug?: string;
      featured_image?: string;
      featured_image_alt?: string;
      featured_image_caption?: string;
    },
  ) =>
    api.post<ApiResponse<{ html: string } | { error: string }>>(`/v1/sites/${siteSlug}/posts/preview-full`, data),
};

// Generic content rows use SQLite 0/1 values; native Switch requires booleans.
function pageFlags(page: Page): Page {
  return { ...page, show_title: Boolean(page.show_title ?? true), show_in_header: Boolean(page.show_in_header ?? true), show_in_footer: Boolean(page.show_in_footer ?? true), noindex: Boolean(page.noindex ?? false) };
}
function pageItem(response: ItemResponse<Page>): ItemResponse<Page> {
  return response.data ? { ...response, data: pageFlags(response.data) } : response;
}

/** Reviewed raw page fields for an atomic conditional save; a mismatch returns 409. */
export interface ExpectedPage {
  content: string | null;
  custom_css: string | null;
  editor_mode: PageEditorMode | null;
}

/** Pages API */
export const pagesApi = {
  /** List pages with pagination */
  list: (siteSlug: string, params: ListParams = {}) =>
    api.get<ListResponse<Page>>(`/v1/sites/${siteSlug}/content/pages${buildQuery(params)}`).then((response) => ({ ...response, data: response.data?.map(pageFlags) })),

  /** Get a single page by ID */
  get: (siteSlug: string, id: string) =>
    api.get<ItemResponse<Page>>(`/v1/sites/${siteSlug}/content/pages/${id}`).then(pageItem),

  /** Get a page by slug */
  getBySlug: (siteSlug: string, slug: string) =>
    api.get<ItemResponse<Page>>(`/v1/sites/${siteSlug}/content/pages/slug/${slug}`).then(pageItem),

  /** Create a new page */
  create: (siteSlug: string, data: ContentData<Page>) =>
    api.post<ItemResponse<Page>>(`/v1/sites/${siteSlug}/content/pages`, data).then(pageItem),

  /** Update an existing page */
  update: (siteSlug: string, id: string, data: ContentData<Page> & { expected_page?: ExpectedPage }) =>
    api.put<ItemResponse<Page>>(`/v1/sites/${siteSlug}/content/pages/${id}`, data).then(pageItem),

  /** Delete a page */
  delete: (siteSlug: string, id: string) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/content/pages/${id}`),

  /** Preview a Tera template with full SSG context */
  previewTera: (siteSlug: string, template: string) =>
    api.post<ApiResponse<{ html: string } | { error: string }>>(`/v1/sites/${siteSlug}/pages/preview-tera`, { template }),

  /** Preview a full page with base.html wrapper */
  previewFull: (siteSlug: string, data: {
    title: string;
    content: string;
    slug?: string;
    editor_mode?: PageEditorMode;
    template?: string | null;
    render_only?: boolean;
    custom_css?: string;
    custom_js?: string;
    show_title?: boolean;
  }) =>
    api.post<ApiResponse<{ html: string } | { error: string }>>(`/v1/sites/${siteSlug}/pages/preview-full`, data),
};

/** Resize mode for images */
export type ResizeMode = 'replace' | 'create_new';

/** Media API */
export const mediaApi = {
  /** List media items */
  list: (siteSlug: string, params: MediaListParams = {}) =>
    api.get<ListResponse<Media>>(`/v1/sites/${siteSlug}/media${buildMediaQuery(params)}`),

  /** Get a single media item */
  get: (siteSlug: string, id: string) =>
    api.get<ItemResponse<Media>>(`/v1/sites/${siteSlug}/media/${id}`),

  /** Get media by URL (backend extracts storage_path using site's storage config) */
  getByUrl: async (siteSlug: string, url: string, storageType?: StorageType): Promise<Media | null> => {
    // Remove cache buster query params
    const cleanUrl = url.split('?')[0];

    // Let the backend handle URL parsing - it knows the site's storage configuration
    // Pass storage_type hint to help backend parse the URL correctly
    const params = new URLSearchParams({ url: cleanUrl });
    if (storageType) {
      params.append('storage_type', storageType);
    }
    const result = await api.get<ListResponse<Media>>(
      `/v1/sites/${siteSlug}/media?${params.toString()}`
    );
    return result.data?.[0] || null;
  },

  /** Upload a file */
  upload: (siteSlug: string, file: File, folder: MediaFolder = 'root'): Promise<ItemResponse<Media>> => {
    const formData = new FormData();
    formData.append('file', file);

    return api.post<ItemResponse<Media>>(
      `/v1/sites/${siteSlug}/media?folder=${encodeURIComponent(folder)}`,
      formData,
    );
  },

  /** Keep a staged Baxter image in the site library. */
  promote: (siteSlug: string, id: string) =>
    api.post<ItemResponse<Media>>(`/v1/sites/${siteSlug}/media/${id}/promote`, {}),

  /**
   * Delete a media item. `afterVersions`: if saved versions still use it, remove it from
   * the library now and let the server delete the stored file once no version uses it.
   */
  delete: (siteSlug: string, id: string, opts?: { afterVersions?: boolean }) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/media/${id}${opts?.afterVersions ? '?after_versions=true' : ''}`),

  /** Delete multiple media items (`afterVersions` as for `delete`). */
  deleteBulk: (siteSlug: string, ids: string[], opts?: { afterVersions?: boolean }) =>
    api.delete<ApiResponse<{ deleted_count: number }>>(`/v1/sites/${siteSlug}/media/batch`, opts?.afterVersions ? { ids, after_versions: true } : { ids }),

  /** Rename a media item */
  rename: (siteSlug: string, id: string, name: string) =>
    api.put<{ success: boolean; name: string }>(`/v1/sites/${siteSlug}/media/${id}`, { name }),

  /** Resize an image */
  resize: (siteSlug: string, id: string, width: number, height: number, mode: ResizeMode) =>
    api.post<ItemResponse<Media>>(`/v1/sites/${siteSlug}/media/${id}/resize`, { width, height, mode }),

  /** Check which posts/pages reference the given media items */
  checkUsage: (siteSlug: string, ids: string[]) =>
    api.post<ApiResponse<Record<string, Array<{ type: string; id: string; title: string }>>>>(`/v1/sites/${siteSlug}/media/check-usage`, { ids }),
};

/** Tags API */
export const tagsApi = {
  /** List all tags */
  list: (siteSlug: string, params: ListParams = {}) =>
    api.get<ListResponse<Tag>>(`/v1/sites/${siteSlug}/content/tags${buildQuery(params)}`),

  /** Get a single tag by ID */
  get: (siteSlug: string, id: string) =>
    api.get<ItemResponse<Tag>>(`/v1/sites/${siteSlug}/content/tags/${id}`),

  /** Create a new tag */
  create: (siteSlug: string, data: ContentData<Tag>) =>
    api.post<ItemResponse<Tag>>(`/v1/sites/${siteSlug}/content/tags`, data),

  /** Delete a tag */
  delete: (siteSlug: string, id: string) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/content/tags/${id}`),
};

/** Post Tags API */
export const postTagsApi = {
  /** Get tags for a post */
  getForPost: (siteSlug: string, postId: string) =>
    api.get<ListResponse<Tag>>(`/v1/sites/${siteSlug}/posts/${postId}/tags`),

  /** Set tags for a post */
  setForPost: (siteSlug: string, postId: string, tagIds: string[]) =>
    api.put<ApiResponse<null>>(`/v1/sites/${siteSlug}/posts/${postId}/tags`, { tag_ids: tagIds }),
};

/**
 * Generic Content Items API
 * Works with any content type (including custom types)
 */
export const contentItemsApi = {
  /** List items in a content type */
  list: (siteSlug: string, typeSlug: string, params: ListParams = {}) =>
    api.get<ListResponse<Record<string, unknown>>>(`/v1/sites/${siteSlug}/content/${typeSlug}${buildQuery(params)}`),

  /** Get a single item by ID */
  get: (siteSlug: string, typeSlug: string, id: string) =>
    api.get<ItemResponse<Record<string, unknown>>>(`/v1/sites/${siteSlug}/content/${typeSlug}/${id}`),

  /** Create a new item - data is sent directly in body (backend uses serde flatten) */
  create: (siteSlug: string, typeSlug: string, data: Record<string, unknown>) =>
    api.post<ItemResponse<Record<string, unknown>>>(`/v1/sites/${siteSlug}/content/${typeSlug}`, data),

  /** Update an item - data is sent directly in body (backend uses serde flatten) */
  update: (siteSlug: string, typeSlug: string, id: string, data: Record<string, unknown>) =>
    api.put<ItemResponse<Record<string, unknown>>>(`/v1/sites/${siteSlug}/content/${typeSlug}/${id}`, data),

  /** Delete an item */
  delete: (siteSlug: string, typeSlug: string, id: string) =>
    api.delete<ApiResponse<null>>(`/v1/sites/${siteSlug}/content/${typeSlug}/${id}`),
};

/** These conflicts reject the entire delete request before any selected object is removed. */
export function retainedMediaDeleteMessage(error: unknown): string | null {
  const response = error as { status?: number; data?: { code?: string; details?: { version_count?: number } } } | null;
  if (response?.status !== 409) return null;
  if (response.data?.code === 'MEDIA_RETAINED_BY_VERSIONS') {
    const count = response.data.details?.version_count;
    const versions = typeof count === 'number' ? `${count} saved version${count === 1 ? '' : 's'}` : 'Saved versions';
    return `No selected files were deleted. ${versions} of your site still use${count === 1 ? 's' : ''} them, and restoring a version needs its files.`;
  }
  if (response.data?.code === 'MEDIA_RETENTION_REVIEW_REQUIRED') return 'No selected files were deleted. Saved-version integrity needs review before deletion can continue.';
  return null;
}
